import { format } from "date-fns";

import { numberPhaseSteps } from "../phaseSteps.js";

export const CUSTOM_PROJECTS_TUTORIAL_PREFERENCE_KEY = "custom_projects";
export const CUSTOM_PROJECTS_TUTORIAL_PARAM = "tutorial";
export const CUSTOM_PROJECTS_TUTORIAL_VALUE = "custom-projects";
export const CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM = "tutorialPhase";
export const CUSTOM_PROJECTS_TUTORIAL_STEP_PARAM = "tutorialStep";

export const CUSTOM_PROJECTS_TUTORIAL_PHASES = {
  PROJECTS_OVERVIEW: "projects-overview",
  TEMPLATES_OVERVIEW: "templates-overview",
  TEMPLATE_MODAL_SETUP: "template-modal-setup",
  TEMPLATE_MODAL_EXAMPLE: "template-modal-example",
  TEMPLATE_CREATED: "template-created",
  PROJECT_CREATE_TRIGGER: "project-create-trigger",
  PROJECT_MODAL_SETUP: "project-modal-setup",
  PROJECT_MODAL_TEMPLATE: "project-modal-template",
  PROJECT_CARD: "project-card",
};

export const CUSTOM_PROJECTS_TUTORIAL_INTRO = {
  title: "Antes de empezar",
  description:
    "Este tutorial te enseña a crear proyectos personalizados a partir de plantillas, usando un ejemplo simulado.",
  badge: "Ejemplo guiado",
  highlightTitle: "Vas a recorrer el flujo completo sin tocar datos reales",
  highlightDescription:
    "Durante el tutorial verás pantallas y modales con datos de ejemplo. No se creará ninguna plantilla ni ningún proyecto real.",
  bulletTitle: "Durante el tutorial",
  bullets: [
    "Verás cómo se organiza la tabla de proyectos personalizados: búsqueda, estados y vistas.",
    "Si eres administrador de la agencia, crearemos una plantilla de ejemplo con fases y tareas.",
    "Crearemos un proyecto ficticio a partir de esa plantilla para ver el flujo completo.",
  ],
  note:
    "Al terminar sabrás cuándo conviene usar una plantilla, cómo se crea un proyecto a partir de ella y qué se puede cambiar después.",
  confirmLabel: "Ver tutorial",
};

export const CUSTOM_PROJECTS_TUTORIAL_COMPLETION_DESCRIPTION =
  "Has completado el tutorial de proyectos personalizados. Ya sabes cómo se estructuran las plantillas, cómo crear un proyecto a partir de una de ellas y qué implica que el proyecto dependa de esa plantilla.";

const TUTORIAL_TODAY = format(new Date(), "yyyy-MM-dd");

export const CUSTOM_PROJECTS_TUTORIAL_CLIENT = {
  value: "tutorial-client",
  label: "Clínica Aurora",
};

export const CUSTOM_PROJECTS_TUTORIAL_TEMPLATE = {
  value: "tutorial-template",
  label: "Onboarding SEO Local",
  description:
    "Plantilla base para arrancar un proyecto personalizado con fases y tareas ya definidas.",
};

export const CUSTOM_PROJECTS_TUTORIAL_TEMPLATE_FORM = {
  name: "Onboarding SEO Local",
  description:
    "Plantilla para organizar la puesta en marcha inicial del cliente con una estructura cerrada.",
  creationMode: "phases",
  phases: [
    {
      id: null,
      name: "Preparación",
      description: "Recogida de información y orden inicial del proyecto.",
      icon: "tabler-target",
      initial_state: "not_started",
      order: 1,
      only_tasks: false,
      tasks: [
        {
          id: null,
          name: "Recoger accesos del cliente",
          description: "Google Business Profile, Search Console y Analytics.",
          initial_state: "planeado",
          documentation_urls: [],
          days_to_deadline: "",
          order: 1,
        },
        {
          id: null,
          name: "Definir objetivos iniciales",
          description: "",
          initial_state: "planeado",
          documentation_urls: [],
          days_to_deadline: "",
          order: 2,
        },
      ],
    },
    {
      id: null,
      name: "Implementación",
      description: "Bloque operativo con las primeras entregas del proyecto.",
      icon: "tabler-rocket",
      initial_state: "not_started",
      order: 2,
      only_tasks: false,
      tasks: [
        {
          id: null,
          name: "Optimizar ficha principal",
          description: "",
          initial_state: "planeado",
          documentation_urls: [],
          days_to_deadline: "",
          order: 1,
        },
        {
          id: null,
          name: "Subir primeras recomendaciones",
          description: "",
          initial_state: "planeado",
          documentation_urls: [],
          days_to_deadline: "",
          order: 2,
        },
      ],
    },
  ],
};

export const CUSTOM_PROJECTS_TUTORIAL_PROJECT_FORM = {
  client: CUSTOM_PROJECTS_TUTORIAL_CLIENT.value,
  useTemplate: "yes",
  template: CUSTOM_PROJECTS_TUTORIAL_TEMPLATE.value,
  isVisibleToClient: true,
  startDate: TUTORIAL_TODAY,
  name: "Implantación SEO Clínica Aurora",
  description:
    "Proyecto ficticio para explicar cómo se crea un proyecto personalizado a partir de una plantilla.",
};

export const CUSTOM_PROJECTS_TUTORIAL_TEMPLATE_CARD = {
  id: "tutorial-template-card",
  name: CUSTOM_PROJECTS_TUTORIAL_TEMPLATE.label,
  description: CUSTOM_PROJECTS_TUTORIAL_TEMPLATE.description,
  template_type: "custom",
  agency: "tutorial-agency",
  can_edit: true,
  updated_at: TUTORIAL_TODAY,
  phases_count: 2,
  tasks_count: 4,
};

export const CUSTOM_PROJECTS_TUTORIAL_PROJECT = {
  id: "tutorial-project-card",
  name: CUSTOM_PROJECTS_TUTORIAL_PROJECT_FORM.name,
  template_name: CUSTOM_PROJECTS_TUTORIAL_TEMPLATE.label,
  client_name: CUSTOM_PROJECTS_TUTORIAL_CLIENT.label,
  state: "not_started",
  progress_percentage: 0,
  start_date: TUTORIAL_TODAY,
  is_internal: false,
  project_type: "custom",
};

const baseStep = { placement: "auto", disableBeacon: true };

// ── Fase 1 · Proyectos (todos los roles) ───────────────────────────────────
const PROJECTS_TOOLBAR_STEP = {
  ...baseStep,
  target: '[data-tutorial="projects-toolbar"]',
  title: "Buscar, filtrar y cambiar de vista",
  content:
    "Desde esta barra buscas proyectos por nombre o cliente, los filtras por estado (en progreso, no iniciados o completados) y abres los filtros avanzados. A la derecha eliges cómo verlos: Kanban, tarjetas o tabla. Durante el tutorial usamos la vista de tabla.",
};

const PROJECTS_LIST_STEP = {
  ...baseStep,
  target: '[data-tutorial="projects-list"]',
  title: "Proyectos personalizados",
  content:
    "Aquí aparecen los proyectos personalizados de tus clientes, uno por fila: trabajos puntuales que no forman parte de la recurrencia mensual. Para el ejemplo arrancamos con la tabla vacía.",
};

export const CUSTOM_PROJECTS_OVERVIEW_STEPS = [
  PROJECTS_TOOLBAR_STEP,
  PROJECTS_LIST_STEP,
  {
    ...baseStep,
    target: '[data-tutorial="projects-templates-button"]',
    title: "Plantillas",
    content:
      "Antes de crear un proyecto conviene tener una plantilla. Una plantilla guarda la estructura de fases y tareas, y cada proyecto que crees a partir de ella la hereda. Vamos a crear una de ejemplo.",
  },
];

const CUSTOM_PROJECTS_OVERVIEW_STEPS_CONSULTANT = [
  PROJECTS_TOOLBAR_STEP,
  PROJECTS_LIST_STEP,
  {
    ...baseStep,
    target: '[data-tutorial="projects-templates-button"]',
    title: "Plantillas",
    content:
      "Aquí consultas las plantillas disponibles. Las crea y mantiene el administrador de tu agencia (o la central): guardan una estructura de fases y tareas que heredan los proyectos creados a partir de ellas.",
  },
];

// ── Fases 2-4 · Plantillas (solo agency_admin) ─────────────────────────────
export const CUSTOM_PROJECTS_TEMPLATES_OVERVIEW_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="project-templates-list"]',
    title: "Listado de plantillas",
    content:
      "Aquí verás las plantillas que puedes usar para crear proyectos: las de tu agencia y las globales que comparte la central. En el ejemplo partimos de una tabla vacía.",
  },
  {
    ...baseStep,
    target: '[data-tutorial="project-templates-new-button"]',
    title: "Nueva plantilla",
    content:
      "Con este botón creas una plantilla nueva. Vamos a abrir el editor para ver cómo se define.",
  },
];

export const CUSTOM_PROJECTS_TEMPLATE_MODAL_SETUP_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="template-editor-basic-fields"]',
    title: "Nombre y descripción",
    content:
      "Empieza poniendo un nombre reconocible y una breve descripción. Es lo que verás después al elegir la plantilla en el modal de nuevo proyecto.",
  },
  {
    ...baseStep,
    target: '[data-tutorial="template-editor-structure-mode"]',
    title: "Estructura del proyecto",
    content:
      "Elige cómo se organizará: por fases, cada una con sus tareas, o como una lista simple de tareas sin fases. Para el ejemplo usaremos fases con tareas.",
  },
];

export const CUSTOM_PROJECTS_TEMPLATE_MODAL_EXAMPLE_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="template-editor-example-phases"]',
    title: "Fases y tareas",
    content:
      "Hemos rellenado una plantilla de ejemplo con dos fases. Cada fase tiene nombre y descripción, y agrupa sus tareas; en cada tarea puedes añadir una descripción y enlaces de documentación. Con «Añadir tarea» y «Añadir fase» amplías la estructura.",
  },
  {
    ...baseStep,
    target: '[data-tutorial="template-editor-submit"]',
    title: "Crear plantilla",
    content:
      "Cuando la estructura esté lista, guárdala con este botón. En el tutorial simularemos la creación sin guardar nada.",
  },
];

export const CUSTOM_PROJECTS_TEMPLATE_CREATED_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="tutorial-template-row"]',
    title: "Plantilla creada",
    content:
      "La plantilla ya aparece en la tabla; en «Estructura» ves su número de fases y tareas. Puedes reutilizarla en tantos proyectos como quieras. Ahora volvemos a Proyectos para crear uno a partir de ella.",
  },
];

// ── Fases 5-8 · Crear el proyecto (todos los roles) ────────────────────────
export const CUSTOM_PROJECTS_CREATE_TRIGGER_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="projects-new-button"]',
    title: "Nuevo proyecto",
    content:
      "Con la plantilla lista, este botón abre el formulario para crear un proyecto personalizado a partir de ella.",
  },
];

const CUSTOM_PROJECTS_CREATE_TRIGGER_STEPS_CONSULTANT = [
  {
    ...baseStep,
    target: '[data-tutorial="projects-new-button"]',
    title: "Nuevo proyecto",
    content:
      "Desde este botón creas un proyecto personalizado para uno de tus clientes. Vamos a abrir el formulario con un ejemplo.",
  },
];

export const CUSTOM_PROJECTS_PROJECT_MODAL_SETUP_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="create-project-primary-fields"]',
    title: "Cliente, plantilla y visibilidad",
    content:
      "Elige el cliente, indica si quieres partir de una plantilla y decide si el proyecto será visible para el cliente. Si lo ocultas, quedará solo para uso interno del equipo y no tendrá conversación con el cliente.",
  },
];

export const CUSTOM_PROJECTS_PROJECT_MODAL_TEMPLATE_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="create-project-template-selector"]',
    title: "Seleccionar plantilla",
    content:
      "Escoge la plantilla que servirá de base. El proyecto copiará sus fases y tareas.",
  },
  {
    ...baseStep,
    target: '[data-tutorial="create-project-details"]',
    title: "Detalles del proyecto",
    content:
      "Ajusta el nombre (si lo dejas vacío se usa el de la plantilla), la fecha de inicio y una descripción opcional para el equipo.",
  },
  {
    ...baseStep,
    target: '[data-tutorial="create-project-submit"]',
    title: "Crear proyecto",
    content:
      "Con plantilla, este botón crea el proyecto directamente. Si eliges no usar plantilla, el botón pasa a «Definir proyecto» y abre el mismo editor de fases y tareas para montar la estructura desde cero. En el tutorial simularemos la creación.",
  },
];

export const CUSTOM_PROJECTS_PROJECT_CARD_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="tutorial-project-row"]',
    title: "Proyecto creado desde plantilla",
    content:
      "El proyecto ya aparece en la tabla con su cliente, su plantilla y su estado. Desde «Progreso» entrarás en su detalle para actualizar el estado, las fechas y los datos de cada fase y tarea, pero no podrás añadir ni quitar fases o tareas: la estructura es la de la plantilla. Si necesitas otra, ajusta la plantilla y crea un proyecto nuevo, o créalo sin plantilla.",
  },
];

// ── Secuencia completa por rol ─────────────────────────────────────────────
const isConsultantRole = (role) => role === "consultant";

const getCustomProjectsPhaseSequence = (role) => {
  const P = CUSTOM_PROJECTS_TUTORIAL_PHASES;
  if (isConsultantRole(role)) {
    return [
      [P.PROJECTS_OVERVIEW, CUSTOM_PROJECTS_OVERVIEW_STEPS_CONSULTANT],
      [P.PROJECT_CREATE_TRIGGER, CUSTOM_PROJECTS_CREATE_TRIGGER_STEPS_CONSULTANT],
      [P.PROJECT_MODAL_SETUP, CUSTOM_PROJECTS_PROJECT_MODAL_SETUP_STEPS],
      [P.PROJECT_MODAL_TEMPLATE, CUSTOM_PROJECTS_PROJECT_MODAL_TEMPLATE_STEPS],
      [P.PROJECT_CARD, CUSTOM_PROJECTS_PROJECT_CARD_STEPS],
    ];
  }
  return [
    [P.PROJECTS_OVERVIEW, CUSTOM_PROJECTS_OVERVIEW_STEPS],
    [P.TEMPLATES_OVERVIEW, CUSTOM_PROJECTS_TEMPLATES_OVERVIEW_STEPS],
    [P.TEMPLATE_MODAL_SETUP, CUSTOM_PROJECTS_TEMPLATE_MODAL_SETUP_STEPS],
    [P.TEMPLATE_MODAL_EXAMPLE, CUSTOM_PROJECTS_TEMPLATE_MODAL_EXAMPLE_STEPS],
    [P.TEMPLATE_CREATED, CUSTOM_PROJECTS_TEMPLATE_CREATED_STEPS],
    [P.PROJECT_CREATE_TRIGGER, CUSTOM_PROJECTS_CREATE_TRIGGER_STEPS],
    [P.PROJECT_MODAL_SETUP, CUSTOM_PROJECTS_PROJECT_MODAL_SETUP_STEPS],
    [P.PROJECT_MODAL_TEMPLATE, CUSTOM_PROJECTS_PROJECT_MODAL_TEMPLATE_STEPS],
    [P.PROJECT_CARD, CUSTOM_PROJECTS_PROJECT_CARD_STEPS],
  ];
};

/**
 * Pasos de una fase con la numeración global ya calculada para el rol.
 * Devuelve [] si la fase no forma parte del recorrido de ese rol.
 */
export const getCustomProjectsPhaseSteps = (phase, role) => {
  return numberPhaseSteps(getCustomProjectsPhaseSequence(role), phase);
};
